/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
// Production-only deterrents against casually copying the site's content or
// opening the browser's inspector: right-click, text selection/copy, save /
// view-source / dev-tools shortcuts, and a dev-tools-open overlay.
//
// This is a speed bump, not security — anything a browser can display can
// still be extracted by someone determined (or with JavaScript disabled, or
// via a scraper that never opens a UI). Real protection for data is
// server-side. Editable fields, and the /admin console, are exempt.

const EDITABLE = 'input, textarea, select, [contenteditable="true"], [contenteditable=""]';
const CLASS_NAME = 'protect-content';

const isAdminRoute = () => window.location.pathname.startsWith('/admin');
const isEditable = (target) => !!(target && target.closest && target.closest(EDITABLE));
const active = () => !isAdminRoute();

function syncClass() {
    document.documentElement.classList.toggle(CLASS_NAME, active());
}

function isBlockedShortcut(e) {
    const key = (e.key || '').toLowerCase();
    const ctrl = e.ctrlKey || e.metaKey;
    if (e.key === 'F12')
        return true;
    // Inspect / console / element picker (Windows, Linux, macOS, Firefox).
    if (ctrl && e.shiftKey && ['i', 'j', 'c', 'k', 'm'].includes(key))
        return true;
    if (e.metaKey && e.altKey && ['i', 'j', 'c', 'u'].includes(key))
        return true;
    // View source, save page.
    if (ctrl && ['u', 's'].includes(key))
        return true;
    return false;
}

function isBlockedCopyShortcut(e) {
    const key = (e.key || '').toLowerCase();
    return (e.ctrlKey || e.metaKey) && ['c', 'x', 'a'].includes(key);
}

let overlay = null;
function setDevtoolsOverlay(visible) {
    if (visible && !overlay) {
        overlay = document.createElement('div');
        overlay.setAttribute('role', 'alert');
        overlay.style.cssText = 'position:fixed;inset:0;z-index:2147483647;background:#0f172a;color:#fff;display:flex;align-items:center;justify-content:center;text-align:center;padding:24px;font:600 16px system-ui,sans-serif';
        overlay.textContent = 'Developer tools are not permitted on this site. Please close them to continue.';
        document.body.appendChild(overlay);
    }
    else if (!visible && overlay) {
        overlay.remove();
        overlay = null;
    }
}

// A `debugger` statement only pauses execution while dev tools are open, so
// a long gap around it means the inspector is open. Unlike window-size
// heuristics this doesn't misfire for browser side panels or zoom.
function devtoolsOpen() {
    const start = performance.now();
    // eslint-disable-next-line no-new-func
    new Function('debugger')();
    return performance.now() - start > 120;
}

export function enableContentProtection() {
    if (typeof window === 'undefined')
        return;
    if (process.env.NODE_ENV !== 'production' || process.env.REACT_APP_CONTENT_PROTECTION === 'off')
        return;

    syncClass();
    // SPA route changes: re-evaluate whether the current path is protected.
    for (const method of ['pushState', 'replaceState']) {
        const original = window.history[method];
        window.history[method] = function patched(...args) {
            const result = original.apply(this, args);
            syncClass();
            return result;
        };
    }
    window.addEventListener('popstate', syncClass);

    const block = (e) => { e.preventDefault(); };
    document.addEventListener('contextmenu', (e) => { if (active() && !isEditable(e.target))
        block(e); });
    document.addEventListener('copy', (e) => { if (active() && !isEditable(e.target))
        block(e); });
    document.addEventListener('cut', (e) => { if (active() && !isEditable(e.target))
        block(e); });
    document.addEventListener('dragstart', (e) => { if (active() && !isEditable(e.target))
        block(e); });
    document.addEventListener('selectstart', (e) => { if (active() && !isEditable(e.target))
        block(e); });
    document.addEventListener('keydown', (e) => {
        if (!active())
            return;
        if (isBlockedShortcut(e) || (isBlockedCopyShortcut(e) && !isEditable(e.target)))
            block(e);
    });

    setInterval(() => {
        if (!active()) {
            setDevtoolsOverlay(false);
            return;
        }
        setDevtoolsOverlay(devtoolsOpen());
    }, 1000);
}
